import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';
import { NewAdvancePettyCash } from '../types/accounting';
import { User } from '../types/user';

interface AddPettyCashModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  paidToUser: User | null;
}

const AddPettyCashModal: React.FC<AddPettyCashModalProps> = ({ open, onClose, onSave, paidToUser }) => {
  const [newAdvance, setNewAdvance] = useState<Partial<NewAdvancePettyCash>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewAdvance((prev: Partial<NewAdvancePettyCash>) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setNewAdvance((prev: Partial<NewAdvancePettyCash>) => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
    }
  };

  const handleSubmit = async () => {
    if (!paidToUser || !newAdvance.amount || !newAdvance.date) {
      setError('Amount and date are required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const advanceData: NewAdvancePettyCash = {
        ...newAdvance,
        amount: Number(newAdvance.amount),
        paid_to: paidToUser.id,
        date: newAdvance.date,
      };
      await accountingService.createAdvancePettyCash(advanceData);
      onSave();
      onClose();
      setNewAdvance({}); // Reset form
    } catch (err: any) {
      console.error('Failed to create advance:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Petty Cash for {paidToUser?.first_name}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField margin="normal" required fullWidth label="Amount" name="amount" type="number" onChange={handleChange} />
          <DatePicker
            label="Date"
            value={newAdvance.date ? new Date(newAdvance.date) : null}
            onChange={handleDateChange}
            sx={{ width: '100%', mt: 2, mb: 1 }}
          />
          <TextField margin="normal" fullWidth label="Notes" name="notes" multiline rows={3} onChange={handleChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPettyCashModal;
